import { defineStore } from 'pinia'
import type { BodySite, KitchenTake, MemberStats, SleepSource } from '@refuelled/shared'
import { ref } from 'vue'
import { ApiError, api, requestBlob, upload } from '@/api/client'
import type { Nutrients } from '@/api/types'
import { usePhoneHealthStore } from '@/stores/phoneHealth'

export interface MemberTarget {
  energyKcal: number
  proteinG: number
  fatG: number
  carbG: number
  fibreG: number | null
  sugarG: number | null
  trainingDayEnergyKcal: number | null
  trainingDayCarbG: number | null
  source: 'computed' | 'manual'
  effectiveFrom: string
}

/** One day's readings for a person, from the scales, a watch, a cuff or a hand. */
export interface DailyMetric {
  date: string
  source: string
  weightKg: number | null
  bodyFatPct: number | null
  fatMassKg: number | null
  muscleMassKg: number | null
  hydrationKg: number | null
  boneMassKg: number | null
  visceralFat: number | null
  systolicMmhg: number | null
  diastolicMmhg: number | null
  spo2Pct: number | null
  hrMinBpm: number | null
  hrAvgBpm: number | null
  hrMaxBpm: number | null
  restingHrDerived: boolean
  temperatureC: number | null
  pulseWaveVelocity: number | null
  restingHr: number | null
  steps: number | null
  distanceM: number | null
  activeEnergyKj: number | null
  sleepMin: number | null
  sleepDeepMin: number | null
  sleepLightMin: number | null
  sleepRemMin: number | null
  sleepHrAvg: number | null
}

export interface Member {
  id: string
  userId: string | null
  displayName: string
  sex: 'female' | 'male' | 'unspecified'
  dob: string | null
  heightCm: number | null
  activityLevel: 'sedentary' | 'light' | 'moderate' | 'very' | 'extra'
  goal: 'lose' | 'maintain' | 'gain' | 'performance'
  goalRate: number | null
  /** What they are aiming for, so the deficit can stop. Null for most people. */
  targetWeightKg: number | null
  /** A daily step target, or null for none. There is no default (rule 2). */
  stepTarget: number | null
  /** Minutes a night they are aiming for; null for none. */
  sleepTargetMin: number | null
  /** Their own daily target for drinks in ml; null uses the default (ADR-039). */
  fluidTargetMl: number | null
  /** The target that applies: theirs, or the default for their age and sex. */
  fluidTargetEffectiveMl: number
  /** The sleep source family they trust; null for automatic (ADR-034). */
  sleepSource: string | null
  /** The share of a day's exercise added to its allowance, 0 to 1 (ADR-035). */
  eatBackFraction: number
  /** Derived from the date of birth by the API, never sent (ADR-015). */
  isChild: boolean
  latestWeightKg: number | null
  /** When the person left the household; null while they are in it. */
  archivedAt: string | null
  /** True when a picture is stored; fetch it with avatarUrl(). */
  hasAvatar: boolean
  target: MemberTarget | null
  constraints: Array<{
    id: string
    kind: 'allergen' | 'exclusion' | 'dislike' | 'preference'
    value: string
    hard: boolean
  }>
}

export interface DailyTotals {
  date: string
  memberId: string
  memberName: string
  consumed: Nutrients
  target: MemberTarget | null
  expenditureKcal: number | null
  readings: {
    sleepMin: number | null
    sleepDeepMin: number | null
    /** Naps that day, apart from the night; null when none. */
    napMin: number | null
    steps: number | null
    restingHr: number | null
    weightKg: number | null
    systolicMmhg: number | null
    diastolicMmhg: number | null
  }
  /** The day's allowance: the target plus a share of its exercise (ADR-035). */
  allowance?: {
    baseKcal: number
    recordedKcal: number
    plannedKcal: number
    eatBackFraction: number
    addedKcal: number
    energyKcal: number
    carbG: number
  } | null
  isTrainingDay: boolean
  meals: Array<{
    id: string
    slot: MealSlot
    title: string
    quantity: number
    unit: string
    energyKcal: number | null
    source: string
    /** Their own figure, which nothing can recompute (ADR-017). */
    estimated: boolean
    planSlotId: string | null
    foodId: string | null
    recipeId: string | null
    proteinG: number | null
    carbG: number | null
    fatG: number | null
    sugarG: number | null
    fibreG: number | null
    saltG: number | null
    /** Millilitres, for a drink whose amount is known (ADR-039). */
    fluidMl?: number | null
  }>
  /** What was drunk against the day's target (ADR-039). */
  fluid?: { ml: number; targetMl: number; unknownCount: number } | null
  /** How much of `consumed` was asserted rather than computed. */
  estimatedKcal: number
  /** Planned for this person today, with whatever they have said about it. */
  planned: Array<{
    planSlotId: string
    slot: MealSlot
    title: string
    /** Planned as typed text: nothing to log from (ADR-042). */
    freeText?: boolean
    servings: number | null
    grams: number | null
    state: 'offered' | 'accepted' | 'declined'
    slotStatus: 'planned' | 'cooked' | 'skipped'
    logged: boolean
  }>
}

export type MealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'drink' | 'custom'

/** The parts the day is divided into. `custom` sits with the snacks; drinks come last (ADR-039). */
export const MEAL_SLOTS = [
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'dinner', label: 'Dinner' },
  { key: 'snack', label: 'Snacks' },
  { key: 'drink', label: 'Drinks' },
] as const

export const useMembersStore = defineStore('members', () => {
  const list = ref<Member[]>([])
  const error = ref<string | null>(null)

  function fail(err: unknown, fallback: string) {
    error.value = err instanceof ApiError ? err.message : fallback
  }

  /** Those who have left, for the People page's Archived section only. */
  const archived = ref<Member[]>([])

  async function load() {
    try {
      list.value = await api.get<Member[]>('/members')
    } catch (err) {
      fail(err, 'Could not load the household')
    }
  }

  async function loadArchived() {
    const everyone = await api.get<Member[]>('/members', { includeArchived: 'true' })
    // A server from before archiving sends no field at all; that is nobody archived.
    archived.value = everyone.filter((m) => m.archivedAt != null)
  }

  /** Someone leaves (M-03): hidden from today, history kept, sign-in disabled. */
  async function archive(memberId: string) {
    await api.post(`/members/${memberId}/archive`)
    await Promise.all([load(), loadArchived()])
  }

  async function unarchive(memberId: string) {
    await api.post(`/members/${memberId}/unarchive`)
    await Promise.all([load(), loadArchived()])
  }

  /** Pictures, as object URLs, one per member and size; released with the store. */
  const avatarUrls = ref<Record<string, string>>({})

  async function avatarUrl(memberId: string, size: 'thumb' | 'display' = 'thumb'): Promise<string | null> {
    const key = `${memberId}:${size}`
    if (avatarUrls.value[key]) return avatarUrls.value[key]!
    const blob = await requestBlob(`/members/${memberId}/avatar`, { size })
    if (!blob) return null
    const url = URL.createObjectURL(blob)
    avatarUrls.value[key] = url
    return url
  }

  function forgetAvatar(memberId: string) {
    for (const [key, url] of Object.entries(avatarUrls.value)) {
      if (key.startsWith(`${memberId}:`)) {
        URL.revokeObjectURL(url)
        delete avatarUrls.value[key]
      }
    }
  }

  async function setAvatar(memberId: string, file: Blob) {
    await upload(`/members/${memberId}/avatar`, file, 'avatar.jpg')
    forgetAvatar(memberId)
    await load()
  }

  async function removeAvatar(memberId: string) {
    await api.delete(`/members/${memberId}/avatar`)
    forgetAvatar(memberId)
    await load()
  }

  /** Gone for good; only for a person with no sign-in. */
  async function remove(memberId: string) {
    await api.delete(`/members/${memberId}`)
    await Promise.all([load(), loadArchived()])
  }

  /** The families one person's sleep comes from, to choose between (ADR-034). */
  async function sleepSources(memberId: string) {
    return api.get<SleepSource[]>(`/members/${memberId}/sleep-sources`)
  }

  async function save(input: Partial<Member>, id?: string) {
    const payload = {
      displayName: input.displayName,
      sex: input.sex ?? 'unspecified',
      dob: input.dob || null,
      heightCm: input.heightCm ?? null,
      activityLevel: input.activityLevel ?? 'moderate',
      goal: input.goal ?? 'maintain',
      goalRate: input.goalRate ?? null,
      // An empty box means no target, not zero.
      targetWeightKg: input.targetWeightKg || null,
      stepTarget: input.stepTarget || null,
      // Sent only by a form that shows it, so another cannot clear it.
      ...('sleepTargetMin' in input ? { sleepTargetMin: input.sleepTargetMin || null } : {}),
      // Empty goes back to the default for their age and sex.
      ...('fluidTargetMl' in input ? { fluidTargetMl: input.fluidTargetMl || null } : {}),
      ...('sleepSource' in input ? { sleepSource: input.sleepSource || null } : {}),
      ...(typeof input.eatBackFraction === 'number' ? { eatBackFraction: input.eatBackFraction } : {}),
    }
    const member = id
      ? await api.put<Member>(`/members/${id}`, payload)
      : await api.post<Member>('/members', payload)
    await load()
    return member
  }

  async function recordWeight(memberId: string, date: string, weightKg: number) {
    await api.post(`/members/${memberId}/metrics`, { date, weightKg })
    await load()
  }

  /** Every reading a person has, newest last; body figures decrypted by the API and nowhere else. */
  async function metrics(memberId: string, days = 90) {
    return api.get<DailyMetric[]>(`/members/${memberId}/metrics`, { days })
  }

  /** The Statistics page's day series (M-81, M-82). */
  async function stats(memberId: string, days = 30) {
    return api.get<MemberStats>(`/members/${memberId}/stats`, { days })
  }

  /**
   * Tape measurements over a window, for charting a waist (M-115).
   *
   * Member-scoped, because Statistics is a page about a person who may not
   * be the one reading it. The self-only range endpoint cannot serve it.
   */
  async function bodyMeasurements(memberId: string, days = 90) {
    return api.get<
      Array<{ date: string; site: BodySite; millimetres: number; source: string }>
    >(`/members/${memberId}/body-measurements`, { days })
  }

  async function weightTrend(memberId: string, days = 90) {
    return api.get<{
      points: Array<{ date: string; weightKg: number }>
      trend: Array<{ date: string; weightKg: number }>
      rateKgPerWeek: number | null
    }>(`/members/${memberId}/weight-trend`, { days })
  }

  async function addConstraint(
    memberId: string,
    input: { kind: string; value: string; hard: boolean },
  ) {
    await api.post(`/members/${memberId}/constraints`, input)
    await load()
  }

  async function removeConstraint(memberId: string, constraintId: string) {
    await api.delete(`/members/${memberId}/constraints/${constraintId}`)
    await load()
  }

  async function setManualTarget(
    memberId: string,
    target: Omit<MemberTarget, 'source' | 'effectiveFrom'>,
  ) {
    await api.put(`/members/${memberId}/targets`, target)
    await load()
  }

  async function recompute(memberId: string, force = false) {
    const result = await api.post<{ computed: boolean; reason?: string }>(
      `/members/${memberId}/targets/recompute${force ? '?force=true' : ''}`,
    )
    await load()
    return result
  }

  async function day(date: string, memberId?: string) {
    return api.get<DailyTotals[]>(`/logs/day/${date}`, { memberId })
  }

  /**
   * Two records, one human (ADR-015). Everything the source holds moves onto
   * `keepId` and the source is deleted; where both held the same thing, the
   * one being kept wins.
   */
  /** Say whether a planned meal happened for you (ADR-016, ADR-017). */
  async function sayItHappened(planSlotId: string, happened: boolean) {
    await api.post(`/plan-slots/${planSlotId}/attendance`, { happened })
  }

  /** Put something in a slot: a recipe, a food, or something free-form. */
  async function logMeal(input: {
    memberId: string
    date: string
    slot: MealSlot
    /** The planned meal this is what it turned out to be (ADR-042). */
    planSlotId?: string | null
    recipeId?: string
    foodId?: string
    quantity: number
    unit: 'g' | 'ml' | 'serving' | 'item'
    freeForm?: {
      description: string
      energyKcal: number
      proteinG?: number | null
      carbG?: number | null
      fatG?: number | null
      saveAsFoodGrams?: number | null
    }
    /** Take what was eaten out of the kitchen's stock of the food. */
    takeFromKitchen?: boolean
  }) {
    const logged = await api.post<{ id: string; kitchen: KitchenTake | null }>('/logs/meals', input)
    usePhoneHealthStore().mealsChanged()
    return logged
  }

  /** Correct an entry in place: its slot, how much, or the figures given for a free-form one. */
  async function updateMeal(
    logId: string,
    patch: {
      slot?: MealSlot
      quantity?: number
      freeForm?: {
        description: string
        energyKcal: number
        proteinG?: number | null
        carbG?: number | null
        fatG?: number | null
      } | null
    },
  ) {
    await api.patch(`/logs/meals/${logId}`, patch)
    usePhoneHealthStore().mealsChanged()
  }

  /** Keep one slot's logged foods as a recipe of one serving. */
  async function mealToRecipe(input: { memberId: string; date: string; slot: MealSlot; title: string }) {
    return api.post<{ recipeId: string; title: string; ingredients: number; skipped: number; withPhoto: boolean }>(
      '/logs/meals/to-recipe',
      input,
    )
  }

  /** A glass or a bottle of water, as a drink (ADR-039). */
  async function logWater(memberId: string, date: string, ml: number) {
    await api.post<{ id: string }>('/logs/water', { memberId, date, ml })
    usePhoneHealthStore().mealsChanged()
  }

  async function deleteMeal(logId: string) {
    await api.delete(`/logs/meals/${logId}`)
    usePhoneHealthStore().mealsChanged()
  }

  async function merge(keepId: string, fromMemberId: string) {
    const result = await api.post<{ moved: Record<string, number> }>(`/members/${keepId}/merge`, {
      fromMemberId,
    })
    await load()
    return result
  }

  return {
    sleepSources,
    list,
    error,
    load,
    save,
    recordWeight,
    metrics,
    bodyMeasurements,
    weightTrend,
    stats,
    archived,
    loadArchived,
    avatarUrl,
    setAvatar,
    removeAvatar,
    archive,
    unarchive,
    remove,
    addConstraint,
    removeConstraint,
    setManualTarget,
    recompute,
    day,
    merge,
    sayItHappened,
    logMeal,
    updateMeal,
    mealToRecipe,
    logWater,
    deleteMeal,
  }
})
